import { isPlatformBrowser } from '@angular/common';
import { Component, HostListener, OnInit, PLATFORM_ID, OnDestroy, AfterViewInit, inject } from '@angular/core';
import { WelcomeAlsuperModalComponent } from './../welcome-alsuper-modal/welcome-alsuper-modal.component';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { EMAIL_REGEX } from '../../utils';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { TokenService } from 'src/app/services/token.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LocalstorageService } from 'src/app/services/localstorage.service';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GoogleCredentialResponse } from 'src/app/models/google.model';

import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';

declare let gtag: any;
declare let google: any;
@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: true,
  imports: [MatIconModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatDividerModule, TranslateModule, MatSnackBarModule]
})
export class SigninComponent implements OnInit, OnDestroy, AfterViewInit {
  public loading = false;
  public displayRestorePassword = false;
  public displayToolbar = false;
  public hide = true;
  public signinForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
    password: new UntypedFormControl('', [Validators.required, Validators.minLength(8)])
  });
  public restorePasswordForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)])
  });
  private subs = new Subject<void>();

  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.isBrowser) this.displayToolbar = window.innerWidth <= 900;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<SigninComponent>,
    private dialog: MatDialog,
    private recaptchaV3Service: ReCaptchaV3Service,
    private tokenStorage: TokenService,
    private localStorageService: LocalstorageService
  ) { }

  ngOnInit(): void {
    if (this.isBrowser) this.displayToolbar = window.innerWidth <= 900;
    this.signinForm.get('email')?.valueChanges.pipe(
      takeUntil(this.subs),
      distinctUntilChanged()
    ).subscribe((res: string) => {
      const email = res.toLowerCase().trim().replace(/\s/g, '');
      this.signinForm.patchValue({ email });
    });
    this.signinForm.get('password')?.valueChanges.pipe(
      takeUntil(this.subs),
      distinctUntilChanged()
    ).subscribe((res: string) => {
      const password = res.trim().replace(/\s/g, '');
      this.signinForm.patchValue({ password });
    });
    this.restorePasswordForm.get('email')?.valueChanges.pipe(
      takeUntil(this.subs),
      distinctUntilChanged()
    ).subscribe((res: string) => {
      const email = res.toLowerCase().trim().replace(/\s/g, '');
      this.restorePasswordForm.patchValue({ email });
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      let buttonSize = '328';
      if (window.innerWidth <= 450 && window.innerWidth > 425) {
        buttonSize = '278';
      } else if (window.innerWidth <= 425 && window.innerWidth > 375) {
        buttonSize = '260';
      } else if (window.innerWidth <= 375) {
        buttonSize = '220';
      }
      google.accounts.id.initialize({
        client_id: environment.googleClientID,
        callback: (response: GoogleCredentialResponse) => {
          this.onLoginGoogle(response.credential);
        }
      });
      google.accounts.id.renderButton(
        document.getElementById('googleButton'),
        { theme: 'outline', size: 'large', width: buttonSize, text: 'signin_with', locale: 'mx' }
      );
      google.accounts.id.prompt();
    }
  }

  async signin(): Promise<void> {
    this.recaptchaV3Service.execute('register')
      .pipe(
        takeUntil(this.subs)
      )
      .subscribe(async (token: string) => {
        if (token) {
          const { data } = await this.authService.verifyReCaptcha(token);
          if (data?.message === 'Token verified successfully') {
            const userData = {
              email: this.signinForm.get('email')?.value,
              password: this.signinForm.get('password')?.value
            };
            try {
              const { data } = await this.authService.signin(userData);
              if (data[0]?.message === 'Old client, redirect to sign up.') {
                this.openWelcomeAlsuperModal(data[0]?.email);
              } else {
                if (this.isBrowser) localStorage.setItem('token', JSON.stringify(data[0]));
                this.localStorageService.setProperty('order_rated_modal', true);
                await this.router.navigate(['']);

                // Ensure gtag event is sent before reload
                gtag('event', 'login', { 'method': 'Email' });

                // Use a small delay to ensure gtag has time to send the event
                await new Promise(resolve => setTimeout(resolve, 100));

                if (this.isBrowser) window.location.reload();
              }
            } catch (error: any) {
              switch (error.error.data[0].error) {
                case 'Invalid credentials.':
                  this.signinForm.controls.email.setErrors({});
                  this.signinForm.controls.password.setErrors({ wrongPassword: true });
                  break;
                case 'Invalid user type.':
                  this.snackbarService.openSnackbar('GENERAL.userInvalid', 'warn');
                  break;
                case 'the user is disabled.':
                  this.snackbarService.openSnackbar('GENERAL.userBlocked', 'warn');
                  break;
                case 'You must complete the registration before you can login.':
                  this.signinForm.controls.email.setErrors({ notFound: true });
                  await this.router.navigate(['autenticacion/registro'], { queryParams: { phone: error.error.data[0].cellphone, id: error.error.data[0].user_id } });
                  this.dialogRef.close();
                  break;
                default:
                  if (error.status === 429) {
                    this.snackbarService.openSnackbar('¡Demasiados intentos! Espera 5 minutos e inténtalo de nuevo.', 'warn');
                  }
                  break;
              }
            }
          }
        }
      });
  }

  public openWelcomeAlsuperModal(email: string): void {
    this.dialog.open(WelcomeAlsuperModalComponent, { autoFocus: false, disableClose: true }).afterClosed().subscribe(async (res: any) => {
      if (res) {
        await this.router.navigate(['autenticacion/registro'], { queryParams: { email } });
        this.dialogRef.close();
      }
    });
  }

  public async sendEmailRestorePassword(): Promise<any> {
    this.recaptchaV3Service.execute('register').pipe(
      takeUntil(this.subs)
    ).subscribe(async (token: string) => {
      if (token) {
        try {
          this.loading = true;
          await this.authService.sendEmailRestorePassword({ email: this.restorePasswordForm.get('email')?.value });
          this.loading = false;
          this.restorePasswordForm.get('email')?.reset();
          this.displayRestorePassword = !this.displayRestorePassword;
          this.snackbarService.openSnackbar('AUTH.instructionSend', 'success', true, false, '', false);
        } catch (error) {
          this.loading = false;
          this.restorePasswordForm.controls.email.setErrors({ invalid: true });
        }
      }
    });
  }


  onKeyUp(): void {
    this.signinForm.controls.email.setErrors(null);
  }

  public async onNavigate(route: string): Promise<void> {
    await this.router.navigate([route]);
    this.dialogRef.close();
  }

  private async onLoginGoogle(idToken: string): Promise<void> {
    try {
      const responseSignin = await this.authService.signinGoogle({ firebase_jwt: idToken });
      if (responseSignin?.data[0]?.jwt) {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('token', JSON.stringify(responseSignin.data[0]));
          this.tokenStorage.saveToken(responseSignin.data[0].jwt);
          this.tokenStorage.saveRefreshToken(responseSignin.data[0].refresh_token);
          this.localStorageService.setProperty('order_rated_modal', true);
          await this.router.navigate(['']);

          // Ensure gtag event is sent before reload
          gtag('event', 'login', { 'method': 'Email' });

          // Use a small delay to ensure gtag has time to send the event
          await new Promise(resolve => setTimeout(resolve, 100));

          if (this.isBrowser) window.location.reload();
        }
      } else {
        this.onNavigate('autenticacion/registro');
        this.snackbarService.openSnackbar('AUTH.ERROR.notFound', 'warn');
      }
    } catch (error: any) {
      switch (error.error.data[0].error) {
        case 'Invalid credentials.':
          this.signinForm.controls.email.setErrors({});
          this.signinForm.controls.password.setErrors({ wrongPassword: true });
          break;
        case 'the user is disabled.':
          this.snackbarService.openSnackbar('GENERAL.userBlocked', 'warn');
          break;
        case 'Invalid user type.':
          this.snackbarService.openSnackbar('GENERAL.userInvalid', 'warn');
          break;
        case 'You must complete the registration before you can login.':
          this.signinForm.controls.email.setErrors({ notFound: true });
          break;
        default:
          break;
      }
    }
  }

  public async onLoginFacebook(): Promise<void> {
    this.authService.signout();
    try {
      const accessToken = await this.authService.onOpenFacebookPopup();
      const responseSignin = await this.authService.signinFacebook({ firebase_jwt: accessToken });
      if (responseSignin?.data[0]?.jwt) {
        if (this.isBrowser) localStorage.setItem('token', JSON.stringify(responseSignin.data[0]));
        this.tokenStorage.saveToken(responseSignin.data[0].jwt);
        this.tokenStorage.saveRefreshToken(responseSignin.data[0].refresh_token);
        this.localStorageService.setProperty('order_rated_modal', true);
        await this.router.navigate(['']);
        gtag('event', 'login', { 'method': 'Email' });
        await new Promise(resolve => setTimeout(resolve, 100));
        if (this.isBrowser) window.location.reload();
      } else {
        if (responseSignin?.data?.message === 'User not found.') {
          if (responseSignin?.data?.user_data?.email) {
            const claims = {
              name: responseSignin?.data?.user_data?.name ?? '',
              lastname: responseSignin?.data?.user_data?.lastname ?? '',
              email: responseSignin?.data?.user_data?.email,
              cellphone: '',
              gender: '',
              password: '',
              date_birth: '',
              loginType: 'social',
              type: 'client'
            };
            await this.authService.signup({ claims });
            await this.onLoginFacebook();
          } else {
            this.snackbarService.openSnackbar('SNACKS.facebook_not_linked', 'advice', true, false, undefined, false, false);
          }
        } else {
          this.snackbarService.openSnackbar('SNACKS.facebook_not_linked', 'advice', true, false, undefined, false, false);
        }
      }
    } catch (error: any) {
      switch (error.error.data[0].error) {
        case 'Invalid credentials.':
          this.signinForm.controls.email.setErrors({});
          this.signinForm.controls.password.setErrors({ wrongPassword: true });
          break;
        case 'the user is disabled.':
          this.snackbarService.openSnackbar('GENERAL.userBlocked', 'warn');
          break;
        case 'Invalid user type.':
          this.snackbarService.openSnackbar('GENERAL.userInvalid', 'warn');
          break;
        case 'You must complete the registration before you can login.':
          this.signinForm.controls.email.setErrors({ notFound: true });
          break;
        default:
          break;
      }
    }
  }

  public close(): void {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.subs.next();
    this.subs.complete();
  }

}
